import { RutgersCourse, UserAPCredit, UserPrerequisiteInfo } from '@/app/types/interfaces';
import type { TranscriptEarnedCourse } from '@/app/lib/transcriptParser';

/** Sum credits from unified prerequisite rows (profile prior classes, AP equivalents, overrides). */
export function sumCreditsFromPrerequisiteInfo(info: UserPrerequisiteInfo[]): number {
  return info.reduce((sum, u) => {
    const raw = u.credits;
    const n =
      typeof raw === 'number'
        ? raw
        : parseFloat(String(raw ?? '').replace(/[^0-9.]/g, '')) || 0;
    return sum + n;
  }, 0);
}

/** Credits from plan semesters where a course row is explicitly marked completed (matches degree-planning page). */
export function sumPlanMarkedCompletedCredits(semesters: unknown): number {
  if (!Array.isArray(semesters)) return 0;
  type PlanCourse = { kind?: string; completed?: boolean; credits?: number };
  return semesters.reduce((sum: number, sem: { courses?: unknown[] }) => {
    const raw = sem.courses ?? [];
    const courses = (Array.isArray(raw) ? raw : []) as PlanCourse[];
    return (
      sum +
      courses.reduce((s: number, c: PlanCourse) => {
        if (c.kind === 'course' && c.completed) return s + (Number(c.credits) || 0);
        return s;
      }, 0)
    );
  }, 0);
}

/**
 * Total credits toward the degree for progress bars. Uses plan.totalCredits, then requirements.totalCredits,
 * then 120 when the plan is clearly tied to a degree but totals were never persisted (common Rutgers BS default).
 */
export function resolveDegreePlanDisplayTotalCredits(plan: Record<string, unknown> | null | undefined): number {
  if (!plan) return 0;
  const top = Number(plan.totalCredits) || 0;
  if (top > 0) return top;
  const req = plan.requirements;
  if (req && typeof req === 'object' && !Array.isArray(req)) {
    const t = Number((req as { totalCredits?: number }).totalCredits) || 0;
    if (t > 0) return t;
  }
  const ids = plan.selectedDegreeIds;
  const major = plan.major;
  const hasDegreeContext =
    (Array.isArray(ids) && ids.length > 0) ||
    (typeof major === 'string' && major.trim().length > 0);
  return hasDegreeContext ? 120 : 0;
}
import { convertAPCreditsToCourses } from '@/app/services/prerequisiteChecker';
import { usersApi } from '@/app/services/api';
import { getAPExamById } from '@/app/lib/apCreditData';
import { calculateRutgersGpa } from '@/app/lib/rutgersGpa';
import { summarizeRecordedCredits } from '@/app/lib/academicCreditSummary';

// Note: Supabase calls are funneled through usersApi to leverage caching

/**
 * The catalog contains the large, checked-in term datasets.  Academic data is
 * used by shared account surfaces, so keep that catalog on the interaction
 * path where a stored course identifier actually needs to be hydrated.
 */
async function loadParsedCourses(): Promise<RutgersCourse[]> {
  const { loadCatalogCourses } = await import('@/app/services/courseCatalogClient');
  return loadCatalogCourses();
}

export interface CourseGradeEntry {
  code: string; // e.g. "01:198:111"
  grade: string; // e.g. "A", "B+"
  credits?: number; // optional override credits
  term?: string; // e.g. "Fall 2025"; needed for term-based policy checks
  designation?: 'STANDARD' | 'P' | 'NC' | 'E' | 'J' | 'K' | 'W' | 'IN' | 'TZ' | 'N/A';
  includeInGpa?: boolean;
  countsTowardMajor?: boolean; // student-selected major GPA basis
}

interface LegacyPreviousClassRow {
  course_code: string;
  name?: string;
  credits?: string | number;
  grade?: string;
}

/** Match the grade view produced for legacy rows when academic data is loaded. */
function projectLegacyCourseGrades(rows: LegacyPreviousClassRow[]): CourseGradeEntry[] {
  return rows
    .filter(row => !!row.grade)
    .map(row => ({
      code: row.course_code,
      grade: String(row.grade),
      credits: typeof row.credits === 'string' ? parseFloat(row.credits) : row.credits,
    }));
}

/** Compare the loaded grade view, ignoring JSON object key order. */
function sameGradeValue(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) return true;
  if (!left || !right || typeof left !== 'object' || typeof right !== 'object'
    || Array.isArray(left) !== Array.isArray(right)) return false;
  if (Array.isArray(left) && Array.isArray(right)) {
    return left.length === right.length && left.every((item, index) => sameGradeValue(item, right[index]));
  }
  const leftObject = left as Record<string, unknown>;
  const rightObject = right as Record<string, unknown>;
  const keys = Object.keys(leftObject);
  return keys.length === Object.keys(rightObject).length
    && keys.every(key => Object.prototype.hasOwnProperty.call(rightObject, key)
      && sameGradeValue(leftObject[key], rightObject[key]));
}

export interface GpaPlannerScenarioCourse {
  id: string;
  baseId?: string;
  name: string;
  credits: number;
  grade: string;
  countsTowardMajor?: boolean;
}

export interface GpaPlannerState {
  version: 1;
  currentGpaOverride?: number;
  currentGradedCreditsOverride?: number;
  schoolOverride?: string;
  academicYearOverride?: string;
  termLabel?: string;
  annualDegreeCredits?: number;
  currentTermDegreeCredits?: number;
  cumulativeCompletedCredits?: number;
  cumulativeAttemptedCredits?: number;
  consecutiveTermsBelowTwo?: number;
  scholarshipAward?: string;
  scholarshipGpa?: number;
  scenarioCourses?: GpaPlannerScenarioCourse[];
}

export interface UserAcademicData {
  completedCourses: RutgersCourse[]; // explicit completed courses
  apCredits: UserAPCredit[]; // AP credits with equivalencies
  overrides?: string[]; // courses treated as taken (no grade)
  courseGrades?: CourseGradeEntry[]; // optional grades for GPA
  gpaPlanner?: GpaPlannerState;
  lastUpdated: string;
}

// Storage shape (backward compatible)
export interface PreviousClassesData {
  // New preferred field: store only base IDs to keep payload small and stable
  completedCourseBaseIds?: string[];
  // Back-compat fallbacks that we continue to accept on load
  completedCourses?: Array<string | RutgersCourse>;
  // Minimal AP credit storage: only the examId and the achieved score
  apCredits?: { examId: string; score: number; isApplied?: boolean }[];
  // Back-compat (older interim field name)
  apCreditsIDs?: { examId: string; score: number }[];
  overrides?: string[]; // baseIds without colons
  courseGrades?: CourseGradeEntry[]; // grades by baseId; never store grades for AP-derived credit
  gpaPlanner?: GpaPlannerState;
  lastUpdated: string;
}

function isLegacyPreviousClassRow(value: unknown): value is LegacyPreviousClassRow {
  return !!value && typeof value === 'object' && !Array.isArray(value)
    && typeof (value as { course_code?: unknown }).course_code === 'string';
}

function hydrateLegacyCourse(row: LegacyPreviousClassRow, byBaseId: Map<string, RutgersCourse>): RutgersCourse {
  const code = courseIdKey(row.course_code);
  const found = byBaseId.get(code);
  if (found) return found;
  const dept = `${code.slice(0, 2)}:${code.slice(2, 5)}`;
  const cls = code.slice(5);
  const credits = typeof row.credits === 'string' ? parseFloat(row.credits) : row.credits || 0;
  return {
    baseId: code,
    id: code,
    courseName: row.name || code,
    fullTitle: row.name || code,
    department: dept,
    class_number: cls || '',
    credits,
    campus: 'NB',
    meetingTimes: [],
    instructors: [],
    section: 'LEGACY',
    subjectNotes: null,
    coreRequirements: [],
    prerequisites: undefined,
  } as RutgersCourse;
}

/** Use the same completed-course projection for account loads and GPA save results. */
async function hydrateCompletedCourses(parsedPrev: unknown): Promise<RutgersCourse[]> {
  if (!Array.isArray(parsedPrev) && (!parsedPrev || typeof parsedPrev !== 'object')) return [];

  if (Array.isArray(parsedPrev)) {
    if (parsedPrev.length === 0) return [];
    const byBaseId = new Map((await loadParsedCourses()).map(course => [course.baseId, course]));
    return (parsedPrev as LegacyPreviousClassRow[]).map(row => hydrateLegacyCourse(row, byBaseId));
  }

  const obj = parsedPrev as PreviousClassesData;
  if (Array.isArray(obj.completedCourseBaseIds)) {
    // An explicit empty list means all courses were removed. Retained legacy
    // rows are provenance, not a fallback that can resurrect those courses.
    if (obj.completedCourseBaseIds.length === 0) return [];
    const byBaseId = new Map((await loadParsedCourses()).map(course => [course.baseId, course]));
    // Legacy arrays are promoted on GPA save while their original rows remain
    // in completedCourses. Keep unmatched legacy credits visible after reload.
    const legacyByBaseId = new Map<string, LegacyPreviousClassRow>();
    if (Array.isArray(obj.completedCourses)) {
      for (const row of obj.completedCourses) {
        if (isLegacyPreviousClassRow(row)) {
          legacyByBaseId.set(courseIdKey(row.course_code), row);
        }
      }
    }
    return obj.completedCourseBaseIds
      .map(id => {
        const key = courseIdKey(id);
        const legacyRow = legacyByBaseId.get(key);
        return byBaseId.get(key) ?? (legacyRow ? hydrateLegacyCourse(legacyRow, byBaseId) : undefined);
      })
      .filter(Boolean) as RutgersCourse[];
  }
  if (Array.isArray(obj.completedCourses)) {
    if (obj.completedCourses.length > 0 && typeof obj.completedCourses[0] === 'string') {
      const byBaseId = new Map((await loadParsedCourses()).map(course => [course.baseId, course]));
      return (obj.completedCourses as string[])
        .map(id => byBaseId.get(courseIdKey(id)))
        .filter(Boolean) as RutgersCourse[];
    }
    return obj.completedCourses as RutgersCourse[];
  }
  return [];
}

interface FreshAcademicSnapshot {
  /** Keep this exact value, including JSON-string or SQL-null representation, for CAS. */
  raw: unknown;
  document: Record<string, unknown>;
}

/** Read the complete owner record without a profile cache or display hydration. */
async function readFreshAcademicSnapshot(userId: string): Promise<FreshAcademicSnapshot> {
  if (!userId) throw new Error('An account is required to save academic data');
  const response = await usersApi.getFresh(userId);
  const ownerRow = Array.isArray(response) ? (response.length === 1 ? response[0] : null) : response;
  if (!ownerRow || typeof ownerRow !== 'object' || Array.isArray(ownerRow)
    || ownerRow.uid !== userId || !Object.prototype.hasOwnProperty.call(ownerRow, 'previous_classes')) {
    throw new Error('The current academic record owner could not be verified');
  }
  const raw = ownerRow.previous_classes;
  if (raw === undefined) throw new Error('The academic record is missing');
  const parsed: unknown = typeof raw === 'string' ? JSON.parse(raw) : raw;
  if (parsed === null) return { raw, document: {} };
  if (Array.isArray(parsed)) {
    if (parsed.some(item => !isLegacyPreviousClassRow(item) || !/\d/.test(item.course_code))) {
      throw new Error('The saved academic record has an unsupported legacy format');
    }
    return { raw, document: {
      completedCourseBaseIds: parsed.map(item => courseIdKey(item.course_code)),
      completedCourses: parsed,
      courseGrades: projectLegacyCourseGrades(parsed),
    } };
  }
  if (!parsed || typeof parsed !== 'object') {
    throw new Error('The saved academic record has an unsupported format');
  }
  const document = parsed as Record<string, unknown>;
  const collectionFields = ['completedCourseBaseIds', 'completedCourses', 'apCredits', 'apCreditsIDs', 'overrides', 'courseGrades'];
  if (collectionFields.some(key => Object.prototype.hasOwnProperty.call(document, key) && !Array.isArray(document[key]))
    || (document.gpaPlanner != null && (typeof document.gpaPlanner !== 'object' || Array.isArray(document.gpaPlanner)))) {
    throw new Error('The saved academic record has an unsupported format');
  }
  return { raw, document };
}

/** A focused mutation preserves every unrelated raw field and checks the full document at write time. */
async function mutateAcademicRecord(
  userId: string,
  change: (current: Readonly<Record<string, unknown>>) => Record<string, unknown> | null,
): Promise<boolean> {
  try {
    const { raw, document } = await readFreshAcademicSnapshot(userId);
    const fields = change(document);
    if (fields === null) return true; // Verified owner and an idempotent no-op.
    const payload = { ...document, ...fields, lastUpdated: new Date().toISOString() };
    const result = await usersApi.updateAcademicDataForOwner(userId, payload, raw);
    return result.status === 'saved';
  } catch {
    console.error('Academic record save failed');
    return false;
  }
}

function currentCompletedBaseIds(document: Readonly<Record<string, unknown>>): string[] {
  const ids = document.completedCourseBaseIds;
  if (Array.isArray(ids)) {
    if (!ids.every(id => typeof id === 'string' && id.length > 0)) {
      throw new Error('The completed course IDs cannot be verified');
    }
    return ids;
  }
  const courses = document.completedCourses;
  if (!Array.isArray(courses)) return [];
  return courses.map(course => {
    if (typeof course === 'string') return course;
    if (course && typeof course === 'object' && !Array.isArray(course)) {
      const baseId = (course as { baseId?: unknown }).baseId;
      if (typeof baseId === 'string') return baseId;
      const code = (course as { course_code?: unknown }).course_code;
      if (typeof code === 'string') return courseIdKey(code);
    }
    throw new Error('The saved completed courses cannot be verified');
  });
}

function incomingCourseBaseIds(courses: RutgersCourse[]): string[] {
  if (!Array.isArray(courses) || courses.some(course => !course || typeof course.baseId !== 'string' || !course.baseId)) {
    throw new Error('The edited completed courses cannot be verified');
  }
  return courses.map(course => course.baseId);
}

function courseIdKey(id: string): string {
  if (/^\d{8}$/.test(id)) return id;
  return /^\d{2}[:\-]\d{3}[:\-]\d{3}$/.test(id) ? id.replace(/[^0-9]/g, '') : id;
}

/** Apply only the editor's membership changes to the latest raw list. */
function mergeCompletedCourseEdit(fresh: string[], edited: string[], loaded?: string[]): string[] {
  const baselineKeys = new Set((loaded ?? []).map(courseIdKey));
  const editedKeys = new Set(edited.map(courseIdKey));
  const freshKeys = new Set(fresh.map(courseIdKey));
  if (loaded !== undefined && [...baselineKeys].some(key => !freshKeys.has(key))) {
    throw new Error('A loaded completed course was removed after this editor opened');
  }
  if (loaded === undefined) {
    requireNoUnbasedRemoval(fresh.map(courseIdKey), [...editedKeys], loaded);
  }
  const removed = new Set([...baselineKeys].filter(key => !editedKeys.has(key)));
  const merged = fresh.filter(id => !removed.has(courseIdKey(id)));
  const present = new Set(merged.map(courseIdKey));
  for (const id of edited) {
    const key = courseIdKey(id);
    if (!present.has(key) && (loaded === undefined || !baselineKeys.has(key))) {
      merged.push(id);
      present.add(key);
    }
  }
  return merged;
}

function retainedCompletedRows(
  document: Readonly<Record<string, unknown>>,
  ids: string[],
  originalIds: string[],
): Record<string, unknown> {
  if (!Object.prototype.hasOwnProperty.call(document, 'completedCourses')) return {};
  const rows = document.completedCourses;
  if (!Array.isArray(rows)) throw new Error('The saved completed course rows cannot be verified');
  const selected = new Set(ids.map(courseIdKey));
  const representedBefore = new Set(originalIds.map(courseIdKey));
  const retained = rows.filter(row => {
    const rawId = typeof row === 'string' ? row
      : row && typeof row === 'object' && !Array.isArray(row)
        ? ((row as { baseId?: unknown }).baseId ?? (row as { course_code?: unknown }).course_code)
        : null;
    if (typeof rawId !== 'string') throw new Error('The saved completed course row cannot be verified');
    const key = courseIdKey(rawId);
    // An old row absent from the explicit ID list is provenance we cannot
    // infer the editor meant to remove. Drop only rows for removed list IDs.
    return !representedBefore.has(key) || selected.has(key);
  });
  return { completedCourses: retained };
}

function currentOverrides(document: Readonly<Record<string, unknown>>): string[] {
  const overrides = document.overrides ?? [];
  if (!Array.isArray(overrides) || !overrides.every(code => typeof code === 'string')) {
    throw new Error('The saved course overrides cannot be verified');
  }
  return overrides;
}

function requireNoUnbasedRemoval(current: string[], edited: string[], baseline: unknown): void {
  if (baseline === undefined && current.some(value => !edited.includes(value))) {
    throw new Error('A loaded field baseline is required to remove saved academic entries');
  }
}

type StoredAPEntry = { examId: string; score: number; isApplied?: boolean; [key: string]: unknown };

function currentAPEntries(document: Readonly<Record<string, unknown>>): StoredAPEntry[] {
  const entries = document.apCredits ?? document.apCreditsIDs ?? [];
  if (!Array.isArray(entries) || entries.some(entry => !entry || typeof entry !== 'object' || Array.isArray(entry)
    || typeof entry.examId !== 'string' || typeof entry.score !== 'number')) {
    throw new Error('The saved AP credits cannot be verified');
  }
  return entries as StoredAPEntry[];
}

function apComparison(entries: Array<{ examId: string; score: number; isApplied?: boolean }>): unknown[] {
  return entries.map(entry => ({ examId: entry.examId, score: entry.score, isApplied: entry.isApplied !== false }));
}

function updatedAPEntries(credits: UserAPCredit[], current: StoredAPEntry[]): StoredAPEntry[] {
  if (!Array.isArray(credits) || credits.some(entry => !entry || typeof entry.examId !== 'string'
    || typeof entry.score !== 'number')) throw new Error('The edited AP credits cannot be verified');
  const byExamId = new Map(current.map(entry => [String(entry.examId), entry]));
  const selected = credits.map(entry => ({
    ...(byExamId.get(entry.examId) ?? {}),
    examId: entry.examId,
    score: entry.score,
    isApplied: typeof entry.isApplied === 'boolean'
      ? entry.isApplied : byExamId.get(entry.examId)?.isApplied !== false,
  }));
  // An unrecognized saved exam cannot appear in the hydrated editor. Keep it
  // when a caller replaces the visible AP list without a complete raw view.
  const selectedIds = new Set(credits.map(entry => entry.examId));
  return [...selected, ...current.filter(entry => !selectedIds.has(String(entry.examId))
    && !getAPExamById(String(entry.examId)))];
}

function currentGradeEntries(document: Readonly<Record<string, unknown>>): CourseGradeEntry[] {
  const grades = document.courseGrades ?? [];
  if (!Array.isArray(grades) || grades.some(entry => !entry || typeof entry !== 'object' || Array.isArray(entry)
    || typeof entry.code !== 'string' || typeof entry.grade !== 'string')) {
    throw new Error('The saved course grades cannot be verified');
  }
  return grades as CourseGradeEntry[];
}

function updatedGradeEntries(
  grades: CourseGradeEntry[],
  current: CourseGradeEntry[],
  loadedGrades?: CourseGradeEntry[],
  mode: 'replace' | 'append' = 'replace',
): CourseGradeEntry[] {
  if (!Array.isArray(grades) || grades.some(entry => !entry || typeof entry.code !== 'string'
    || typeof entry.grade !== 'string')) throw new Error('The edited course grades cannot be verified');

  const currentByCode = new Map<string, CourseGradeEntry[]>();
  for (const entry of current) {
    const key = courseIdKey(entry.code);
    currentByCode.set(key, [...(currentByCode.get(key) ?? []), entry]);
  }
  const incomingByCode = new Map<string, CourseGradeEntry[]>();
  for (const entry of grades) {
    const key = courseIdKey(entry.code);
    incomingByCode.set(key, [...(incomingByCode.get(key) ?? []), entry]);
  }

  const attemptKey = (entry: CourseGradeEntry): string | null => {
    const term = typeof entry.term === 'string' ? entry.term.trim().toLowerCase() : '';
    return term ? `${courseIdKey(entry.code)}\u0000${term}` : null;
  };
  const repeated = new Set([...currentByCode].filter(([, entries]) => entries.length > 1).map(([code]) => code));
  const repeatedByAttempt = new Map<string, CourseGradeEntry>();
  for (const code of repeated) {
    const saved = currentByCode.get(code) ?? [];
    const incoming = incomingByCode.get(code) ?? [];
    if (incoming.length === 0 && mode === 'append') continue;
    // A code-keyed UI can collapse repeat attempts. Only a complete editor
    // baseline and distinct term identities may replace those saved rows.
    if (loadedGrades === undefined || incoming.length !== saved.length) {
      throw new Error('Repeated course attempts need a complete loaded grade baseline');
    }
    const identities = new Set<string>();
    for (const entry of saved) {
      const identity = attemptKey(entry);
      if (!identity || identities.has(identity)) throw new Error('Repeated course attempts are ambiguous');
      identities.add(identity);
      repeatedByAttempt.set(identity, entry);
    }
    const incomingIdentities = incoming.map(attemptKey);
    if (incomingIdentities.some(identity => !identity || !identities.has(identity))
      || new Set(incomingIdentities).size !== identities.size) {
      throw new Error('Repeated course attempts were not fully identified');
    }
  }
  if ([...incomingByCode].some(([code, entries]) => entries.length > 1 && !repeated.has(code))) {
    throw new Error('New repeated course attempts need a dedicated transcript editor');
  }

  const byCode = new Map(current.map(entry => [courseIdKey(entry.code), entry]));
  const selected = grades.map(entry => ({
    ...(repeated.has(courseIdKey(entry.code))
      ? repeatedByAttempt.get(attemptKey(entry) ?? '')
      : byCode.get(courseIdKey(entry.code))),
    ...Object.fromEntries(Object.entries(entry).filter(([, value]) => value !== undefined)),
  } as CourseGradeEntry));
  // Without a complete loaded baseline, an omitted fresh entry may be hidden
  // from the caller. Keep it instead of interpreting omission as deletion.
  const selectedCodes = new Set(grades.map(entry => courseIdKey(entry.code)));
  const loadedCodes = new Set((mode === 'replace' ? loadedGrades ?? [] : []).map(entry => courseIdKey(entry.code)));
  return [...selected, ...current.filter(entry => !selectedCodes.has(courseIdKey(entry.code))
    && !loadedCodes.has(courseIdKey(entry.code)))];
}

/**
 * Save user's academic data (completed courses and AP credits) to the database
 */
export async function saveUserAcademicData(
  userId: string,
  completedCourses: RutgersCourse[],
  apCredits: UserAPCredit[],
  loadedBaseIds?: string[],
  loadedAPCredits?: UserAPCredit[],
): Promise<boolean> {
  return mutateAcademicRecord(userId, current => {
    const freshIds = currentCompletedBaseIds(current);
    const editedIds = incomingCourseBaseIds(completedCourses);
    const rawAP = currentAPEntries(current);
    if (loadedAPCredits !== undefined && !sameGradeValue(apComparison(rawAP.filter(entry => getAPExamById(entry.examId))), apComparison(loadedAPCredits))) {
      throw new Error('AP credits changed after this editor loaded');
    }
    requireNoUnbasedRemoval(rawAP.filter(entry => getAPExamById(String(entry.examId))).map(entry => String(entry.examId)),
      apCredits.map(entry => entry.examId), loadedAPCredits);
    const mergedIds = mergeCompletedCourseEdit(freshIds, editedIds, loadedBaseIds);
    return { completedCourseBaseIds: mergedIds, ...retainedCompletedRows(current, mergedIds, freshIds),
      apCredits: updatedAPEntries(apCredits, rawAP) };
  });
}

/** Add only explicit reviewed earned rows; never replace any saved grade/attempt. */
export async function saveTranscriptCourses(
  userId: string, entries: readonly TranscriptEarnedCourse[],
  loadedBaseIds: readonly string[], loadedGrades: readonly CourseGradeEntry[],
  current: () => boolean = () => true,
): Promise<boolean> {
  // Snapshot and validate before crossing any async boundary.
  let rows: TranscriptEarnedCourse[];
  try {
    rows = entries.map(row => {
      if (!row || !/^\d{8}$/.test(row.baseId) || row.status !== 'completed'
        || !['A','B+','B','C+','C','D','P','PA','PASS','S','CR'].includes(row.grade)
        || typeof row.credits !== 'number' || !Number.isFinite(row.credits) || row.credits <= 0) throw Error('Invalid earned transcript row');
      return { baseId: row.baseId, status: 'completed', grade: row.grade, credits: row.credits };
    });
    if (new Set(rows.map(row => row.baseId)).size !== rows.length || !current()) return false;
    const baselineIds = [...loadedBaseIds], baselineGrades = structuredClone(loadedGrades);
    const { raw, document } = await readFreshAcademicSnapshot(userId);
    if (!current()) return false;
    const freshIds = currentCompletedBaseIds(document), freshGrades = currentGradeEntries(document);
    if (!sameGradeValue(freshGrades, [...baselineGrades])
      || baselineIds.some(id => !freshIds.some(fresh => courseIdKey(fresh) === courseIdKey(id)))) return false;
    const legacy = Array.isArray(document.completedCourses)
      ? document.completedCourses.filter(isLegacyPreviousClassRow) : [];
    const additions: CourseGradeEntry[] = [];
    for (const row of rows) {
      const saved = freshGrades.filter(entry => courseIdKey(entry.code) === row.baseId);
      const oldRows = legacy.filter(entry => courseIdKey(entry.course_code) === row.baseId && entry.grade);
      if (saved.length > 1 || oldRows.length > 1 || [...saved, ...projectLegacyCourseGrades(oldRows)].some(entry =>
        entry.grade !== row.grade || (entry.credits !== undefined && entry.credits !== row.credits))) return false;
      // Missing metadata on any existing saved grade needs manual reconciliation.
      if (saved.some(entry => entry.credits === undefined)) return false;
      if (!saved.length) additions.push({ code: row.baseId, grade: row.grade, credits: row.credits });
    }
    const ids = [...freshIds], present = new Set(freshIds.map(courseIdKey));
    for (const row of rows) if (!present.has(row.baseId)) { ids.push(row.baseId); present.add(row.baseId); }
    if (!additions.length && ids.length === freshIds.length) return true;
    if (!current()) return false;
    const result = await usersApi.updateAcademicDataForOwner(userId, {
      ...document, completedCourseBaseIds: ids, courseGrades: [...freshGrades, ...additions],
      lastUpdated: new Date().toISOString(),
    }, raw);
    return current() && result.status === 'saved';
  } catch { return false; }
}

/**
 * Load user's academic data (completed courses and AP credits) from the database
 */
export async function loadUserAcademicData(
  userId: string,
  options: { fresh?: boolean } = {},
): Promise<UserAcademicData | null> {
  try {
    
    // Allow calling with undefined userId, returning empty data
    if (!userId) {
      if (options.fresh) throw new Error('An account is required to read fresh academic data');
      return {
        completedCourses: [],
        apCredits: [],
        lastUpdated: new Date().toISOString()
      };
    }

    const data = options.fresh ? await usersApi.getFresh(userId) : await usersApi.get(userId);

    if (options.fresh) {
      // The edge read uses the active session. A switched account must not
      // populate this caller's GPA planner with another owner's record.
      const ownerRow = Array.isArray(data) ? (data.length === 1 ? data[0] : null) : data;
      if (!ownerRow || typeof ownerRow !== 'object' || Array.isArray(ownerRow)
        || ownerRow.uid !== userId || !Object.prototype.hasOwnProperty.call(ownerRow, 'previous_classes')) {
        throw new Error('The fresh academic record owner could not be verified');
      }
    }


    if (!data) {
      console.log('No user data found, returning default empty data');
      return {
        completedCourses: [],
        apCredits: [],
        lastUpdated: new Date().toISOString()
      };
    }

    // Handle array response (get_user returns an array)
    let userData;
    if (Array.isArray(data)) {
      if (data.length === 0) {
        console.log('Empty array response, returning default empty data');
        return {
          completedCourses: [],
          apCredits: [],
          lastUpdated: new Date().toISOString()
        };
      }
      userData = data[0];
    } else {
      userData = data;
    }

    if (options.fresh && userData.previous_classes === undefined) {
      throw new Error('The fresh academic record is missing');
    }
    if (options.fresh && userData.previous_classes !== null && !userData.previous_classes) {
      throw new Error('The fresh academic record has an unsupported value');
    }
    if (!userData.previous_classes) {
      console.log('No previous_classes found in user data, returning default empty data');
      return {
        completedCourses: [],
        apCredits: [],
        lastUpdated: new Date().toISOString()
      };
    }

    
    // Handle multiple historic formats
    let completedCourses: RutgersCourse[] = [];
    let apCredits: UserAPCredit[] = [];
    let overrides: string[] | undefined;
    let courseGrades: CourseGradeEntry[] | undefined;
    let gpaPlanner: GpaPlannerState | undefined;
    let storedLastUpdated: string | undefined;

    const rawPrev = userData.previous_classes;
    // Case 1: stored as JSON string
    let parsedPrev: unknown = rawPrev;
    if (typeof rawPrev === 'string') {
      try {
        parsedPrev = JSON.parse(rawPrev);
      } catch (err) {
        console.error('Error parsing previous_classes string');
        if (options.fresh) throw err;
      }
    }
    if (options.fresh && parsedPrev !== null && !Array.isArray(parsedPrev)
      && (typeof parsedPrev !== 'object' || !parsedPrev)) {
      throw new Error('The fresh academic record has an unsupported format');
    }
    if (options.fresh && Array.isArray(parsedPrev)
      && parsedPrev.some(row => !isLegacyPreviousClassRow(row) || !/\d/.test(row.course_code))) {
      throw new Error('The fresh legacy academic record has an unsupported format');
    }
    if (options.fresh && parsedPrev && typeof parsedPrev === 'object' && !Array.isArray(parsedPrev)) {
      const fields = parsedPrev as Record<string, unknown>;
      const collections = ['completedCourseBaseIds', 'completedCourses', 'apCredits', 'apCreditsIDs', 'overrides', 'courseGrades'];
      if (collections.some(key => Object.prototype.hasOwnProperty.call(fields, key) && !Array.isArray(fields[key]))
        || (fields.gpaPlanner != null && (typeof fields.gpaPlanner !== 'object' || Array.isArray(fields.gpaPlanner)))) {
        throw new Error('The fresh academic record has an unsupported field');
      }
    }

    // Case 2: legacy array of { course_code, name, credits, grade }
    if (Array.isArray(parsedPrev)) {
      const legacyArr = parsedPrev as LegacyPreviousClassRow[];
      completedCourses = await hydrateCompletedCourses(parsedPrev);
      // Build grades
      courseGrades = projectLegacyCourseGrades(legacyArr);
      apCredits = [];
    } else if (typeof parsedPrev === 'object' && parsedPrev) {
      // Case 3: modern object structure
      const obj = parsedPrev as PreviousClassesData;
      completedCourses = await hydrateCompletedCourses(parsedPrev);
      // Inflate minimal AP entries (either new apCredits or legacy apCreditsIDs) into full UserAPCredit objects
      const minimalAP = (obj.apCredits ?? obj.apCreditsIDs ?? []) as Array<{ examId: string; score: number; isApplied?: boolean }>;
      apCredits = minimalAP
        .map(min => {
          const exam = getAPExamById(min.examId);
          if (!exam) return null;
          const equivalents = exam.rutgersEquivalents.filter(eq => min.score >= eq.minScore && min.score <= eq.maxScore);
          return {
            examId: min.examId,
            examName: exam.name,
            score: min.score,
            rutgersEquivalents: equivalents,
            isApplied: min.isApplied !== false,
          } as UserAPCredit;
        })
        .filter(Boolean) as UserAPCredit[];
      overrides = obj.overrides || [];
      courseGrades = obj.courseGrades || [];
      gpaPlanner = obj.gpaPlanner;
      storedLastUpdated = obj.lastUpdated;
    }

    // A single recorded earned-credit override is authoritative over current catalog metadata.
    // Repeated attempts remain unresolved here rather than choosing one silently.
    const recordedAttempts = new Map<string, CourseGradeEntry[]>();
    for (const entry of courseGrades ?? []) {
      const key = courseIdKey(entry.code);
      recordedAttempts.set(key, [...(recordedAttempts.get(key) ?? []), entry]);
    }
    completedCourses = completedCourses.map(course => {
      const attempts = recordedAttempts.get(courseIdKey(course.baseId));
      const credits = attempts?.length === 1 ? attempts[0].credits : undefined;
      return typeof credits === 'number' && Number.isFinite(credits) && credits > 0 ? { ...course, credits } : course;
    });
    const result: UserAcademicData = {
      completedCourses,
      apCredits,
      overrides,
      courseGrades,
      gpaPlanner,
      lastUpdated: storedLastUpdated || new Date().toISOString(),
    };

    return result;
  } catch (error) {
    console.error('Error loading academic data');
    // Fresh owner-bound callers need the actual failure (including HTTP status),
    // not a null that is indistinguishable from an unavailable record.
    if (options.fresh) throw error;
    return null;
  }
}

/**
 * Save only completed courses to the database
 */
export async function saveCompletedCourses(
  userId: string,
  completedCourses: RutgersCourse[],
  loadedBaseIds?: string[],
): Promise<boolean> {
  return mutateAcademicRecord(userId, current => {
    const freshIds = currentCompletedBaseIds(current);
    const editedIds = incomingCourseBaseIds(completedCourses);
    const mergedIds = mergeCompletedCourseEdit(freshIds, editedIds, loadedBaseIds);
    return { completedCourseBaseIds: mergedIds, ...retainedCompletedRows(current, mergedIds, freshIds) };
  });
}

/**
 * Save only AP credits to the database
 */
export async function saveAPCredits(
  userId: string,
  apCredits: UserAPCredit[],
  loadedAPCredits?: UserAPCredit[],
): Promise<boolean> {
  return mutateAcademicRecord(userId, current => {
    const rawAP = currentAPEntries(current);
    if (loadedAPCredits !== undefined && !sameGradeValue(apComparison(rawAP.filter(entry => getAPExamById(entry.examId))), apComparison(loadedAPCredits))) {
      throw new Error('AP credits changed after this editor loaded');
    }
    requireNoUnbasedRemoval(rawAP.filter(entry => getAPExamById(String(entry.examId))).map(entry => String(entry.examId)),
      apCredits.map(entry => entry.examId), loadedAPCredits);
    return { apCredits: updatedAPEntries(apCredits, rawAP) };
  });
}

/**
 * Add a single completed course to the database
 */
export async function addCompletedCourse(
  userId: string,
  course: RutgersCourse
): Promise<boolean> {
  try {
    const existingData = await loadUserAcademicData(userId);
    if (!existingData) return false;
    const completedCourses = existingData.completedCourses;
    
    // Check if course already exists
    if (completedCourses.some(c => c.id === course.id)) {
      // A cached display can still include a course another writer removed.
      // Verify the live owner document before reporting an idempotent success.
      return await mutateAcademicRecord(userId, current => {
        if (!currentCompletedBaseIds(current).some(id => courseIdKey(id) === courseIdKey(course.baseId))) {
          throw new Error('The completed course changed after this page loaded');
        }
        return null;
      });
    }

    const updatedCourses = [...completedCourses, course];
    return await saveCompletedCourses(userId, updatedCourses, completedCourses.map(item => item.baseId));
  } catch {
    console.error('Error adding completed course');
    return false;
  }
}

/**
 * Remove a completed course from the database
 */
export async function removeCompletedCourse(
  userId: string,
  courseId: string
): Promise<boolean> {
  try {
    const existingData = await loadUserAcademicData(userId);
    if (!existingData) return false;
    const completedCourses = existingData.completedCourses;
    
    const updatedCourses = completedCourses.filter(c => c.id !== courseId);
    return await saveCompletedCourses(userId, updatedCourses, completedCourses.map(item => item.baseId));
  } catch {
    console.error('Error removing completed course');
    return false;
  }
}

/**
 * Add or update AP credits in the database
 */
export async function updateAPCredits(
  userId: string,
  apCredits: UserAPCredit[]
): Promise<boolean> {
  try {
    return await saveAPCredits(userId, apCredits);
  } catch {
    console.error('Error updating AP credits');
    return false;
  }
}

/**
 * Get the total number of credits from completed courses and AP credits
 */
export function calculateTotalCredits(
  completedCourses: RutgersCourse[],
  apCredits: UserAPCredit[]
): number {
  return summarizeRecordedCredits(completedCourses, apCredits).totalCredits;
}

/**
 * Get a summary of the user's academic standing
 */
export function getAcademicSummary(
  completedCourses: RutgersCourse[],
  apCredits: UserAPCredit[]
) {
  return summarizeRecordedCredits(completedCourses, apCredits);
} 

// =========================
// Extensions and helpers
// =========================

/** Save course grade entries (used for GPA) without changing completedCourses */
export async function saveCourseGrades(
  userId: string,
  grades: CourseGradeEntry[],
  loadedGrades?: CourseGradeEntry[],
): Promise<boolean> {
  return mutateAcademicRecord(userId, current => {
    const freshGrades = currentGradeEntries(current);
    if (loadedGrades !== undefined && !sameGradeValue(freshGrades, loadedGrades)) {
      throw new Error('Course grades changed after this editor loaded');
    }
    return { courseGrades: updatedGradeEntries(grades, freshGrades, loadedGrades) };
  });
}

/** Save course overrides (treat as completed without grade) */
export async function saveCourseOverrides(
  userId: string,
  overrides: string[],
  loadedOverrides?: string[],
): Promise<boolean> {
  return mutateAcademicRecord(userId, current => {
    if (!Array.isArray(overrides) || !overrides.every(code => typeof code === 'string')) {
      throw new Error('The edited course overrides cannot be verified');
    }
    const fresh = currentOverrides(current);
    if (loadedOverrides !== undefined && !sameGradeValue(fresh, loadedOverrides)) {
      throw new Error('Course overrides changed after this editor loaded');
    }
    requireNoUnbasedRemoval(fresh, overrides, loadedOverrides);
    return { overrides };
  });
}

/** Accept legacy previousClasses array and save into unified shape */
export async function saveLegacyPreviousClasses(
  userId: string,
  legacy: Array<{ course_code: string; name: string; credits: string | number; grade: string }>,
  loadedGrades?: CourseGradeEntry[],
): Promise<boolean> {
  return mutateAcademicRecord(userId, current => {
    if (!Array.isArray(legacy) || legacy.some(row => !isLegacyPreviousClassRow(row)
      || !/\d/.test(row.course_code) || typeof row.grade !== 'string')) {
      throw new Error('The imported course rows cannot be verified');
    }
    const freshGrades = currentGradeEntries(current);
    if (loadedGrades !== undefined && !sameGradeValue(freshGrades, loadedGrades)) {
      throw new Error('Course grades changed after this import loaded');
    }
    const importedGrades = projectLegacyCourseGrades(legacy);
    if (loadedGrades === undefined && importedGrades.some(entry => {
      const prior = freshGrades.find(saved => courseIdKey(saved.code) === courseIdKey(entry.code));
      return prior && (prior.grade !== entry.grade || prior.credits !== entry.credits);
    })) throw new Error('A loaded grade baseline is required to replace a saved grade');

    const freshIds = currentCompletedBaseIds(current);
    const ids = [...freshIds];
    const present = new Set(freshIds.map(courseIdKey));
    for (const row of legacy) {
      const id = courseIdKey(row.course_code);
      if (!present.has(id)) { ids.push(id); present.add(id); }
    }
    const priorRows = Array.isArray(current.completedCourses) ? current.completedCourses : [];
    const appendedRows = [...priorRows];
    for (const row of legacy) {
      const index = appendedRows.findIndex(saved => isLegacyPreviousClassRow(saved)
        && courseIdKey(saved.course_code) === courseIdKey(row.course_code));
      if (index >= 0) appendedRows[index] = { ...appendedRows[index], ...row };
      else appendedRows.push(row);
    }
    return { completedCourseBaseIds: ids, completedCourses: appendedRows,
      courseGrades: updatedGradeEntries(importedGrades, freshGrades, loadedGrades, 'append') };
  });
}

/** Save account-backed GPA planning inputs without changing the academic record. */
export async function saveGpaPlannerState(
  userId: string,
  gpaPlanner: GpaPlannerState,
  loadedPlanner?: GpaPlannerState,
): Promise<boolean> {
  return mutateAcademicRecord(userId, current => {
    if (!gpaPlanner || typeof gpaPlanner !== 'object' || Array.isArray(gpaPlanner)) {
      throw new Error('The edited GPA planner state cannot be verified');
    }
    if (loadedPlanner !== undefined && !sameGradeValue(current.gpaPlanner, loadedPlanner)) {
      throw new Error('GPA planner state changed after this editor loaded');
    }
    if (loadedPlanner === undefined && current.gpaPlanner !== undefined
      && !sameGradeValue(current.gpaPlanner, gpaPlanner)) {
      throw new Error('A loaded GPA planner baseline is required to replace saved state');
    }
    if (sameGradeValue(current.gpaPlanner, gpaPlanner)) return null;
    return { gpaPlanner };
  });
}

export type GpaPlannerWorkspaceSaveResult =
  | { status: 'saved'; courseGrades: CourseGradeEntry[]; completedCourses: RutgersCourse[] }
  | { status: 'saved'; courseGrades: CourseGradeEntry[]; completedCourses: null; contextWarning: 'catalog-unavailable' }
  | { status: 'conflict' }
  | { status: 'error' };

/** Save planner inputs against a fresh academic record and the loaded grade baseline. */
export async function saveGpaPlannerWorkspace(
  userId: string,
  courseGrades: CourseGradeEntry[],
  gpaPlanner: GpaPlannerState,
  loadedCourseGrades: CourseGradeEntry[],
): Promise<GpaPlannerWorkspaceSaveResult> {
  try {
    if (!Array.isArray(courseGrades) || !Array.isArray(loadedCourseGrades)) {
      throw new Error('The loaded grade baseline is required');
    }
    // Display hydration deliberately excludes unknown catalog entries. Saving
    // a scenario must preserve the raw record instead of serializing that view.
    const response = await usersApi.getFresh(userId);
    const ownerRow = Array.isArray(response) ? (response.length === 1 ? response[0] : null) : response;
    if (!ownerRow || typeof ownerRow !== 'object' || Array.isArray(ownerRow)
      || ownerRow.uid !== userId || !Object.prototype.hasOwnProperty.call(ownerRow, 'previous_classes')) {
      throw new Error('The current academic record could not be verified');
    }
    const raw = ownerRow.previous_classes;
    if (raw === undefined) throw new Error('The academic record is missing');
    const parsed: unknown = typeof raw === 'string' ? JSON.parse(raw) : raw;
    let existing: Record<string, unknown>;
    let freshCourseGrades: CourseGradeEntry[];
    if (parsed === null) {
      existing = {};
      freshCourseGrades = [];
    } else if (Array.isArray(parsed)) {
      // Retain each original legacy row, including unknown codes and metadata;
      // base IDs provide the supported modern completed-course projection.
      if (parsed.some(item => !item || typeof item !== 'object' || Array.isArray(item)
        || typeof item.course_code !== 'string' || !/\d/.test(item.course_code))) {
        throw new Error('The saved academic record has an unsupported format');
      }
      existing = {
        completedCourseBaseIds: parsed.map(item => courseIdKey(item.course_code)),
        completedCourses: parsed,
      };
      freshCourseGrades = projectLegacyCourseGrades(parsed as LegacyPreviousClassRow[]);
    } else if (parsed && typeof parsed === 'object') {
      existing = parsed as Record<string, unknown>;
      const collectionFields = ['completedCourseBaseIds', 'completedCourses', 'apCredits', 'apCreditsIDs', 'overrides', 'courseGrades'];
      if (collectionFields.some(key => Object.prototype.hasOwnProperty.call(existing, key) && !Array.isArray(existing[key]))
        || (existing.gpaPlanner != null && (typeof existing.gpaPlanner !== 'object' || Array.isArray(existing.gpaPlanner)))) {
        throw new Error('The saved academic record has an unsupported format');
      }
      freshCourseGrades = (existing.courseGrades as CourseGradeEntry[] | undefined) ?? [];
    } else {
      throw new Error('The saved academic record has an unsupported format');
    }
    const gradeEdit = !sameGradeValue(courseGrades, loadedCourseGrades);
    if (gradeEdit && !sameGradeValue(freshCourseGrades, loadedCourseGrades)) {
      return { status: 'conflict' };
    }
    const gradesToSave = gradeEdit ? courseGrades : freshCourseGrades;
    const payload = {
      ...existing,
      courseGrades: gradesToSave,
      gpaPlanner,
      lastUpdated: new Date().toISOString(),
    };
    const result = await usersApi.updateAcademicDataForOwner(userId, payload, raw);
    if (result.status === 'conflict') return result;
    // Hydrate the document we just wrote. A fresh read here could see another
    // writer's record, while a catalog outage must not undo a committed save.
    try {
      const completedCourses = await hydrateCompletedCourses(payload);
      return { status: 'saved', courseGrades: gradesToSave, completedCourses };
    } catch {
      console.error('Could not hydrate completed courses after GPA save');
      return { status: 'saved', courseGrades: gradesToSave, completedCourses: null,
        contextWarning: 'catalog-unavailable' };
    }
  } catch {
    console.error('Error in saveGpaPlannerWorkspace');
    return { status: 'error' };
  }
}

/** Build a RutgersCourse list for prerequisite checks, including overrides as lightweight stubs */
export function buildCoursesForPrereq(data: UserAcademicData): RutgersCourse[] {
  const base = data.completedCourses || [];
  const overrides = data.overrides || [];
  if (overrides.length === 0) return base;
  const stubs: RutgersCourse[] = overrides.map(code => {
    const [dept, num] = code.includes(':') ? ((): [string, string] => {
      const parts = code.split(':');
      return [`${parts[0]}:${parts[1]}`, parts[2]];
    })() : [code.slice(0, 5), code.slice(5)];
    return {
      baseId: code,
      id: `override-${code}`,
      courseName: code,
      fullTitle: code,
      department: dept,
      class_number: num,
      credits: 0,
      campus: 'NB',
      meetingTimes: [],
      instructors: [],
      section: 'OVERRIDE',
      subjectNotes: 'Override: treated as completed',
      coreRequirements: [],
      prerequisites: undefined,
    } as RutgersCourse;
  });
  return [...base, ...stubs];
}

/** Compute GPA from courseGrades; falls back to credits from matching completedCourses */
export function calculateGpaFromGrades(data: UserAcademicData): number | null {
  if (!data.courseGrades || data.courseGrades.length === 0) return null;
  const courses = data.courseGrades.map((grade) => {
    let credits = grade.credits ?? 0;
    if (!credits) {
      const match = data.completedCourses.find((course) => course.baseId === grade.code || course.id === grade.code);
      if (match) credits = typeof match.credits === 'string' ? parseFloat(match.credits) : match.credits;
    }
    return {
      credits: Number(credits) || 0,
      grade: grade.grade,
      designation: grade.designation,
      includeInGpa: grade.includeInGpa,
    };
  });
  return calculateRutgersGpa(courses).rawGpa;
}

/** Build Degree-Search friendly prerequisite info entries from unified data */
export async function buildUserPrerequisiteInfo(data: UserAcademicData): Promise<UserPrerequisiteInfo[]> {
  const apAsCourses = convertAPCreditsToCourses(data.apCredits || []);
  
  // Only overrides need catalog credit lookup. A loaded empty history or
  // already-hydrated coursework must not fail because an unused catalog is down.
  const allCourses = data.overrides?.length ? await loadParsedCourses() : [];
  const courseByBaseId = new Map(allCourses.map(c => [c.baseId, c]));
  const normalizeCode = (code: string) => String(code).replace(/[^0-9]/g, '');
  
  const overrides = (data.overrides || []).map(code => {
    // Try to find credits for this override from course data
    const normalized = normalizeCode(code);
    let credits = '0';
    
    // Try multiple formats to find the course
    const formats = [
      code,
      normalized,
      code.replace(/:/g, ''),
      normalized.replace(/^0+/, ''),
      `01${normalized}`,
      `0${normalized}`,
    ];
    
    for (const format of formats) {
      const course = courseByBaseId.get(format);
      if (course && course.credits && course.credits !== 'TBA') {
        const numCredits = Number(course.credits);
        if (!isNaN(numCredits) && numCredits > 0) {
          credits = String(numCredits);
          break;
        }
      }
    }
    
    return {
      course_code: code,
      name: code,
      credits,
      grade: '',
    };
  });
  const completed = (data.completedCourses || []).map(c => ({
    course_code: c.baseId,
    name: c.courseName,
    credits: String(c.credits || ''),
    grade: '',
  }));
  const apEntries = apAsCourses.map(c => ({
    course_code: c.baseId,
    name: c.courseName,
    credits: String(c.credits || ''),
    grade: '',
  }));
  const completedCodes = new Set(completed.map(entry => courseIdKey(entry.course_code)));
  const apOnlyCodes = new Set(apEntries.map(entry => courseIdKey(entry.course_code))
    .filter(code => !completedCodes.has(code)));
  // If we have explicit grades, overlay them
  const gradeMap = new Map((data.courseGrades || []).map(g => [courseIdKey(g.code), g]));
  const merged = [...completed, ...apEntries, ...overrides].map(e => {
    // AP credit has no earned letter grade. A historical grade entry for its
    // equivalent must not change the award's grade or credit value. A separate
    // completed-course record retains its explicit grade when both exist.
    if (apOnlyCodes.has(courseIdKey(e.course_code))) return e;
    const g = gradeMap.get(courseIdKey(e.course_code));
    if (g) {
      return { ...e, grade: g.grade, credits: String(g.credits ?? e.credits) };
    }
    return e;
  });
  // De-duplicate by course_code, prefer entries with grade
  const byCode = new Map<string, UserPrerequisiteInfo>();
  for (const e of merged) {
    const key = courseIdKey(e.course_code);
    const existing = byCode.get(key);
    if (!existing || (e.grade && !existing.grade)) byCode.set(key, e);
  }
  return Array.from(byCode.values());
}

// =========================
// New helpers for minimal storage
// =========================

/** Resolve an array of course base IDs to representative RutgersCourse objects */
export async function getRutgersCoursesByBaseIds(baseIds: string[]): Promise<RutgersCourse[]> {
  const allCourses = await loadParsedCourses();
  const byBaseId = new Map(allCourses.map(c => [c.baseId, c]));
  return baseIds
    .map(id => byBaseId.get(id))
    .filter((c): c is RutgersCourse => Boolean(c));
}
